import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { toast } from "sonner";
import { AlertTriangle, Camera, MapPin, Award, Clock, BadgeCheck, Mail, CheckCircle2 } from "lucide-react";
import { COUNTRIES, TCGS, flagEmoji, type TCG } from "@/lib/marketplace";
import { SecuritySection } from "@/components/SecuritySection";
import { supabase } from "@/integrations/supabase/client";
import { getMyProfile, updateMyProfile } from "@/lib/profile.functions";
import {
  getMyAccountDeletionStatus,
  requestAccountDeletion,
  cancelAccountDeletion,
} from "@/lib/account-deletion.functions";

export const Route = createFileRoute("/_authenticated/account")({
  head: () => ({
    meta: [
      { title: "Account settings — Want To Buy" },
      { name: "description", content: "Manage your Want To Buy account, profile, and data." },
    ],
  }),
  component: AccountPage,
});

function AccountPage() {
  const router = useRouter();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [avatar, setAvatar] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");
  const [country, setCountry] = useState("US");
  const [specs, setSpecs] = useState<TCG[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const [deletion, setDeletion] = useState<{
    requested: boolean;
    scheduledFor: string | null;
  }>({ requested: false, scheduledFor: null });

  const [currentEmail, setCurrentEmail] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState<boolean>(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  async function refreshEmail() {
    const { data } = await supabase.auth.getUser();
    const u = data.user;
    if (!u) return;
    setCurrentEmail(u.email ?? null);
    setEmailVerified(Boolean(u.email_confirmed_at));
    // new_email is set by Supabase while a change is awaiting confirmation
    const nextEmail = (u as unknown as { new_email?: string | null }).new_email ?? null;
    setPendingEmail(nextEmail && nextEmail !== u.email ? nextEmail : null);
  }

  useEffect(() => {
    (async () => {
      try {
        const [p, d] = await Promise.all([getMyProfile(), getMyAccountDeletionStatus()]);
        if (p) {
          setUsername(p.username ?? "");
          setBio(p.bio ?? "");
          setCountry((p.country as string) ?? "US");
          setSpecs(((p.tcg_specialisations as TCG[]) ?? []).filter(Boolean));
          setAvatar(p.avatar_url ?? null);
        }
        setDeletion({ requested: d.requested, scheduledFor: d.scheduledFor });
        await refreshEmail();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to load profile");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleChangeEmail(e: React.FormEvent) {
    e.preventDefault();
    const next = newEmail.trim().toLowerCase();
    if (!next || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (next === (currentEmail ?? "").toLowerCase()) {
      toast.error("That is already your current email");
      return;
    }
    setEmailBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: next });
      if (error) throw error;
      setPendingEmail(next);
      setNewEmail("");
      toast.success(`Confirmation sent to ${next}. Click the link in that email to complete the change.`);
      await refreshEmail();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update email");
    } finally {
      setEmailBusy(false);
    }
  }

  const canDelete = confirmText.trim().toUpperCase() === "DELETE";

  function onPickAvatar(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setAvatar(URL.createObjectURL(f));
    toast.info("Avatar upload is local-only in this build.");
  }

  function toggleSpec(t: TCG) {
    setSpecs((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  }

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await updateMyProfile({
        data: {
          username: username.trim().toLowerCase(),
          bio: bio.trim() || null,
          country: country || null,
          tcg_specialisations: specs as string[],
        },
      });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save profile");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!canDelete || busy) return;
    setBusy(true);
    try {
      const res = await requestAccountDeletion();
      setDeletion({ requested: true, scheduledFor: res.scheduledFor });
      setConfirmOpen(false);
      setConfirmText("");
      toast.success("Account scheduled for deletion. You have 30 days to cancel.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not schedule deletion");
    } finally {
      setBusy(false);
    }
  }

  async function handleCancelDelete() {
    setBusy(true);
    try {
      await cancelAccountDeletion();
      setDeletion({ requested: false, scheduledFor: null });
      toast.success("Account deletion cancelled");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not cancel deletion");
    } finally {
      setBusy(false);
    }
  }

  const countryName = COUNTRIES.find((c) => c.code === country)?.name ?? country;
  const scheduledDate = deletion.scheduledFor
    ? new Date(deletion.scheduledFor).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl space-y-6">
        <header>
          <h1 className="font-display text-3xl font-bold">Account settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage your profile, security, and data.</p>
        </header>

        {deletion.requested && (
          <section className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-5">
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-5 w-5 text-amber-500" />
              <div className="flex-1">
                <h2 className="font-display text-base font-bold">Account scheduled for deletion</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Your account will be permanently deleted on
                  <span className="font-semibold text-foreground"> {scheduledDate}</span>. You can
                  cancel any time before then.
                </p>
                <button
                  onClick={handleCancelDelete}
                  disabled={busy}
                  className="mt-3 rounded-md border border-amber-500/40 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-500/10 disabled:opacity-60 dark:text-amber-300"
                >
                  {busy ? "Cancelling…" : "Cancel deletion"}
                </button>
              </div>
            </div>
          </section>
        )}

        <form onSubmit={saveProfile} className="panel space-y-5 p-5">
          <div>
            <h2 className="font-display text-lg font-bold">Profile</h2>
            <p className="mt-1 text-sm text-muted-foreground">Public info shown to other buyers and sellers.</p>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-full bg-primary/15 text-2xl font-display font-bold text-primary">
                {avatar ? (
                  <img src={avatar} alt="Avatar" className="h-full w-full object-cover" />
                ) : (
                  (username[0] ?? "?").toUpperCase()
                )}
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-1 -right-1 grid h-8 w-8 place-items-center rounded-full border border-border bg-panel text-foreground shadow hover:text-primary"
                aria-label="Upload avatar"
              >
                <Camera className="h-3.5 w-3.5" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onPickAvatar} className="hidden" />
            </div>
            <div className="text-xs text-muted-foreground">
              PNG or JPG, square. We'll auto-crop.
              {avatar && (
                <button type="button" onClick={() => setAvatar(null)} className="ml-2 text-destructive hover:underline">Remove</button>
              )}
            </div>
          </div>

          <Field label="Username">
            <input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))} className="input-base" maxLength={24} />
          </Field>
          <Field label="Bio">
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} className="input-base !h-auto min-h-[80px] py-2" maxLength={160} />
          </Field>

          <Field label={<><MapPin className="inline h-3.5 w-3.5" /> Country</>}>
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="input-base">
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{flagEmoji(c.code)} {c.name}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Display only — used for shipping context. Never shared publicly beyond country level. Currently: <span className="text-foreground">{flagEmoji(country)} {countryName}</span>.
            </p>
          </Field>

          <Field label={<><Award className="inline h-3.5 w-3.5" /> TCG specialisations</>}>
            <div className="flex flex-wrap gap-1.5">
              {TCGS.map((t) => {
                const on = specs.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleSpec(t)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      on
                        ? "border-primary bg-primary/15 text-primary"
                        : "border-border bg-background/40 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Shown on your public profile as tags so buyers know which games you primarily trade.
            </p>
          </Field>

          <div className="flex justify-end">
            <button type="submit" disabled={loading || saving} className="btn-primary px-4 py-2 text-sm disabled:opacity-60">
              {saving ? "Saving…" : "Save profile"}
            </button>
          </div>
        </form>

        <SecuritySection />

        <section className="panel space-y-4 p-5">
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary/15 text-primary">
              <Mail className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-lg font-bold">Email address</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Used for sign-in and account notifications. Only visible to you.
              </p>
            </div>
          </div>

          <div className="rounded-md border border-border bg-background/40 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Current email
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground">{currentEmail ?? "—"}</span>
              {emailVerified ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-500">
                  <CheckCircle2 className="h-3 w-3" /> Verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-500">
                  Pending verification
                </span>
              )}
            </div>
            {pendingEmail && (
              <p className="mt-2 text-xs text-amber-500">
                Change pending: <span className="font-semibold">{pendingEmail}</span>. Click the confirmation link we sent to complete the change.
              </p>
            )}
          </div>

          <form onSubmit={handleChangeEmail} className="space-y-2">
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Change email
              </span>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="new@example.com"
                autoComplete="email"
                className="input-base"
              />
            </label>
            <p className="text-[11px] text-muted-foreground">
              We'll send a confirmation link to the new address. Your email only changes after you click it.
            </p>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={emailBusy || !newEmail.trim()}
                className="btn-primary px-4 py-2 text-sm disabled:opacity-60"
              >
                {emailBusy ? "Sending…" : "Send confirmation"}
              </button>
            </div>
          </form>
        </section>

        <section className="rounded-lg border border-blue-400/40 bg-blue-500/[0.06] p-5">
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-blue-500/15 text-blue-500">
              <BadgeCheck className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-lg font-bold">Seller verification</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Apply for a Verified Business or Verified Store badge. Verified stores appear in the public
                Verified Stores directory and earn higher buyer trust on offers.
              </p>
              <Link
                to="/seller-verification"
                className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-blue-500 px-3.5 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                <BadgeCheck className="h-4 w-4" /> Apply for verification
              </Link>
            </div>
          </div>
        </section>

        {!deletion.requested && (
          <section className="rounded-lg border border-destructive/40 bg-destructive/[0.06] p-5">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-destructive/15 text-destructive">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-lg font-bold">Delete account</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Permanently delete your Want To Buy account, listings, buy orders and message history.
                  You can cancel within 30 days — after that, deletion is permanent and cannot be reversed.
                  Requires no in-flight orders or open disputes.
                </p>
                <button
                  onClick={() => setConfirmOpen(true)}
                  className="mt-4 rounded-md bg-destructive px-3.5 py-2 text-sm font-semibold text-destructive-foreground hover:opacity-90"
                >
                  Delete my account
                </button>
              </div>
            </div>
          </section>
        )}
      </div>

      {confirmOpen && (
        <div
          onClick={() => !busy && setConfirmOpen(false)}
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg border border-border bg-panel p-6 shadow-2xl"
          >
            <h3 className="font-display text-xl font-bold">Delete your account?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              This will queue your account, listings, orders and data for deletion. You'll have
              <span className="font-semibold text-foreground"> 30 days </span> to cancel by signing back in.
            </p>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Type DELETE to confirm
            </label>
            <input
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-background px-3 text-sm focus:border-destructive focus:outline-none focus:ring-2 focus:ring-destructive/30"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)} disabled={busy} className="btn-ghost px-3.5 py-2 text-sm">Cancel</button>
              <button
                onClick={handleDelete}
                disabled={!canDelete || busy}
                className="rounded-md bg-destructive px-3.5 py-2 text-sm font-semibold text-destructive-foreground transition disabled:cursor-not-allowed disabled:opacity-50 hover:opacity-90"
              >
                {busy ? "Deleting…" : "Permanently delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.input-base { width:100%; height:2.5rem; border-radius:0.375rem; border:1px solid var(--color-border); background: var(--color-background); padding: 0 0.75rem; font-size: 0.875rem; color: var(--color-foreground); }
      .input-base:focus { outline: none; border-color: var(--color-primary); box-shadow: 0 0 0 2px color-mix(in oklab, var(--color-primary) 30%, transparent); }`}</style>
    </AppShell>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
